import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { usePhoneCapture } from "../context/PhoneCapture";
import { API_BASE } from "./links";

export const PENDING_BOOKING_KEY = "sdr_booking_intent";

const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

const KIND_CONFIG = {
  call: {
    description: "₹199 counseling call — 30-min 1:1 career fit conversation",
    dismissMsg: "Checkout closed — your call isn't booked yet.",
    onPaid: (orderId) => {
      window.location.href = `/payment/success?order_id=${orderId}&type=call`;
    },
  },
  seat: {
    description: "SaaS Sales Academy — 45-day live SDR/BDR program (founding cohort)",
    dismissMsg: "Checkout closed — your seat isn't reserved yet.",
    onPaid: (orderId) => {
      window.location.href = `/payment/success?order_id=${orderId}`;
    },
  },
};

export const openCheckout = async (kind, source, user, onError, phone) => {
  const cfg = KIND_CONFIG[kind];
  try {
    await axios.post(`${API_BASE}/booking-intent`, { kind, source }, { withCredentials: true });
  } catch {
    // logging failure should never block checkout
  }
  try {
    const res = await axios.post(
      `${API_BASE}/create-order`,
      { source, kind },
      { withCredentials: true }
    );
    const { order_id, amount, currency, key_id } = res.data;
    const prefill = { name: user?.name || "", email: user?.email || "" };
    if (phone) prefill.contact = phone;
    const rzp = new window.Razorpay({
      key: key_id || RAZORPAY_KEY_ID,
      amount,
      currency,
      name: "SDR Growth",
      description: cfg.description,
      order_id,
      prefill,
      theme: { color: "#E4602E" },
      modal: {
        ondismiss: () => onError?.(cfg.dismissMsg),
      },
      handler: async (resp) => {
        try {
          await axios.post(`${API_BASE}/verify-payment`, resp, { withCredentials: true });
          cfg.onPaid(order_id);
        } catch {
          onError?.("Payment couldn't be verified. If money was deducted, reach out with your payment ID.");
        }
      },
    });
    rzp.on("payment.failed", () =>
      onError?.("Payment failed — no money was charged. Please try again.")
    );
    rzp.open();
    return true;
  } catch (e) {
    if (e.response?.status === 409) onError?.(e.response.data.detail);
    else onError?.("Couldn't start checkout — please try again.");
    return false;
  }
};

// Both conversion CTAs: sign-in → one-time phone capture → Razorpay payment →
// call: post-payment thank-you page with booking form / seat: success page
const usePaidIntent = (kind) => {
  const { user, login } = useAuth();
  const ensurePhone = usePhoneCapture();
  return async (source, onError) => {
    if (!user) {
      sessionStorage.setItem(PENDING_BOOKING_KEY, JSON.stringify({ kind, source }));
      login();
      return;
    }
    const phone = await ensurePhone(user);
    if (phone === null) return false; // phone modal closed without choosing
    return openCheckout(kind, source, user, onError, phone || undefined);
  };
};

export const useBookCall = () => usePaidIntent("call");
export const useReserveSeat = () => usePaidIntent("seat");
